/**
 * Pre-registration state for the PUBLIC registration flow.
 *
 * The two pre-registration pages must be completed before the public form
 * (/registration/public/step1|2|3) can be opened:
 *   /registration/public/intro           -> event description + "REGISTER NOW"
 *   /registration/public/privacy-notice  -> privacy notice + required consent checkbox
 *
 * The state is kept in a cookie so it survives reloads and expires together with
 * the "_AlUlaPubAt" registration session (both use a 1 hour window).
 *
 * `document.cookie` is used directly instead of the vue-cookies plugin so these
 * helpers can also be called from outside a component (for example a router guard).
 */

export const PRE_REG_COOKIE = '_AlUlaPreReg';

// 1 hour, matching the _AlUlaPubAt registration session
export const PRE_REG_TTL = 60 * 60;

export const PRE_REG_STAGE_INTRO = 'intro';
export const PRE_REG_STAGE_ACCEPTED = 'accepted';

function readCookie(name) {
  const raw = document.cookie
    .split('; ')
    .find((item) => item.startsWith(name + '='));
  if (!raw) return null;
  return decodeURIComponent(raw.slice(name.length + 1));
}

function writeCookie(name, value, maxAge) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    name + '=' + encodeURIComponent(value) +
    '; Max-Age=' + maxAge +
    '; Path=/' +
    '; SameSite=Lax' + secure;
}

/**
 * Reads the stored pre-registration state.
 * Unreadable or malformed values are treated as "no state at all".
 *
 * @returns {{stage: string, accepted_at: string|null}|null}
 */
export function getPreReg() {
  const raw = readCookie(PRE_REG_COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.stage !== 'string') return null;
    return {
      stage: parsed.stage,
      accepted_at: parsed.accepted_at || null,
    };
  } catch (e) {
    return null;
  }
}

/**
 * The visitor clicked "REGISTER NOW" on the introduction page.
 * Never downgrades an existing consent back to the "intro" stage.
 */
export function markIntroSeen() {
  const current = getPreReg();
  if (current && current.stage === PRE_REG_STAGE_ACCEPTED) return;
  writeCookie(
    PRE_REG_COOKIE,
    JSON.stringify({ stage: PRE_REG_STAGE_INTRO, accepted_at: null }),
    PRE_REG_TTL
  );
}

/**
 * The visitor ticked the consent checkbox and continued.
 * The first acceptance of the session is kept, so re-confirming (the checkbox is
 * pre-checked when a consent already exists) does not rewrite the recorded time.
 */
export function acceptPreReg() {
  const current = getPreReg();
  const acceptedAt =
    current && current.stage === PRE_REG_STAGE_ACCEPTED && current.accepted_at
      ? current.accepted_at
      : new Date().toISOString();

  writeCookie(
    PRE_REG_COOKIE,
    JSON.stringify({ stage: PRE_REG_STAGE_ACCEPTED, accepted_at: acceptedAt }),
    PRE_REG_TTL
  );
}

/** Removes the consent, so the next registration has to go through it again. */
export function clearPreReg() {
  writeCookie(PRE_REG_COOKIE, '', 0);
  clearPreRegReturnRoute();
}

/**
 * The step an invited visitor asked for before the pre-registration gate was
 * satisfied, for example `{ name: 'RegistrationStep1', query: { tk, tyt } }`.
 *
 * Invitation links (/registration/visitor/step1|2|3?tk=&tyt=) open the form
 * directly, but the visitor still has to walk the introduction and privacy
 * notice first. Those pages drop the query string, so the requested step - and
 * its tokens - are parked in their own cookie and replayed once the consent is
 * given. It is a separate cookie so the stage/consent semantics of
 * `_AlUlaPreReg` stay untouched and `getPreReg()` keeps returning null when no
 * consent flow has started yet.
 */
export const PRE_REG_RETURN_COOKIE = '_AlUlaPreRegTo';

/**
 * Remembers the invited step the visitor asked for.
 *
 * @param {{name: string, query?: object}} route
 */
export function setPreRegReturnRoute(route) {
  if (!route || typeof route.name !== 'string') return;
  writeCookie(
    PRE_REG_RETURN_COOKIE,
    JSON.stringify({ name: route.name, query: route.query || {} }),
    PRE_REG_TTL
  );
}

/**
 * Reads the parked invited step.
 * Unreadable, malformed or nameless values are treated as "no parked step".
 *
 * @returns {{name: string, query: object}|null}
 */
export function getPreRegReturnRoute() {
  const raw = readCookie(PRE_REG_RETURN_COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.name !== 'string') return null;
    return {
      name: parsed.name,
      query: parsed.query && typeof parsed.query === 'object' ? parsed.query : {},
    };
  } catch (e) {
    return null;
  }
}

/** Drops the parked invited step, so it can never be replayed twice. */
export function clearPreRegReturnRoute() {
  writeCookie(PRE_REG_RETURN_COOKIE, '', 0);
}

/** @returns {boolean} true when the privacy notice has been accepted. */
export function isPreRegAccepted() {
  return getPreReg()?.stage === PRE_REG_STAGE_ACCEPTED;
}

/**
 * Pushes the consent cookie's expiry forward without changing the recorded
 * acceptance time, so a registration that is still in progress is not
 * interrupted when the cookie reaches the end of its window between two steps.
 *
 * Only an already accepted consent is renewed; an `intro` stage cookie is left
 * alone so a visitor who never reached the privacy notice still expires.
 */
export function renewPreReg() {
  const current = getPreReg();
  if (!current || current.stage !== PRE_REG_STAGE_ACCEPTED) return;
  writeCookie(PRE_REG_COOKIE, JSON.stringify(current), PRE_REG_TTL);
}

/**
 * Session cookie written by the first step of the public registration wizard.
 * Declared here so the router can seed it before the wizard runs.
 */
export const REGISTRATION_COOKIE = '_AlUlaPubAt';

/**
 * An invitation link (for example the companion invitation) arrives as
 * /registration/public/step1?tk=<token>. That step immediately redirects through the
 * pre-registration pages, which drop the query string, so the token has to be stored
 * before the redirect happens.
 *
 * The cookie is replaced - never merged - whenever the incoming token differs from
 * the stored one, so a fresh invitation cannot inherit another session's prefill.
 *
 * @param {string|null|undefined} token
 */
export function captureInvitationToken(token) {
  if (!token) return;

  const current = readCookie(REGISTRATION_COOKIE);
  let currentToken = null;
  if (current) {
    try {
      currentToken = JSON.parse(current)?.token_key || null;
    } catch (e) {
      currentToken = null;
    }
  }

  if (currentToken === token) return;

  writeCookie(
    REGISTRATION_COOKIE,
    JSON.stringify({ token_key: token }),
    PRE_REG_TTL
  );
}

/**
 * Media-guest registration session cookie. Declared here so the media wizard's
 * steps share one name for it instead of repeating the literal.
 */
export const MEDIA_SESSION_COOKIE = '_AlUlaMU';

/**
 * The types.slug the media-guest registration flow belongs to.
 */
export const MEDIA_TYPE_SLUG = 'media-guest';

/**
 * Invitation query of a media-guest link.
 *
 * Media guests have two entry points on the same three steps:
 *   /registration/media-guest/step1                  -> public, token free
 *   /registration/media-guest/step1?tyt=&tk=          -> one person's unique link
 *
 * The second shape is the same `tyt` (visitor type token) + `tk` (visitor token)
 * pair the speaker, VIP and other invited types use, so the media form can
 * prefill and then update that person's existing record instead of creating a
 * second one.
 *
 * The media steps are not behind the public pre-registration gate, so unlike the
 * invited visitor flow these params stay in the URL and are simply carried from
 * one step to the next.
 *
 * @param {{query?: object}|null|undefined} route
 * @returns {{tk: string, tyt: string}|null} null for the public, token-free entry
 */
export function getMediaInviteQuery(route) {
  const query = route?.query || {};
  const tk = query.tk;
  const tyt = query.tyt;

  if (!tk || !tyt) return null;

  return { tk: String(tk), tyt: String(tyt) };
}

/**
 * Route name the public registration steps must redirect to when the
 * pre-registration gate has not been passed, or null when it has been.
 *
 * @returns {string|null}
 */
export function preRegRedirectRoute() {
  const pre = getPreReg();
  if (!pre) return 'RegistrationPublicIntro';
  if (pre.stage !== PRE_REG_STAGE_ACCEPTED) return 'RegistrationPublicPrivacyNotice';
  return null;
}
